import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import logo from '@/assets/logob.png';

interface NavItem {
  label: string;
  href: string;
  external?: boolean;
}

const navItems: NavItem[] = [
  { label: '首页', href: '/' },
  { label: '关于OPC', href: '/about' },
  { label: '平台能力', href: '/capabilities' },
  { label: '优贝科技', href: 'http://www.ubestcar.com/', external: true },
  { label: '创业伙伴', href: '/partners' },
  { label: '联系我们', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [longPressProgress, setLongPressProgress] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const pressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pressStartRef = useRef<number>(0);
  const isPlatformPage = location.pathname.startsWith('/platform');
  const isLoginPage = location.pathname === '/login' || location.pathname === '/forgot-password';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const clearLongPress = useCallback(() => {
    if (pressTimerRef.current) {
      clearInterval(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    setLongPressProgress(0);
  }, []);

  // 提取桌面端和移动端重复的 navItems 渲染逻辑
  const renderNavItem = (item: NavItem, textCls: string, options?: { mobile?: boolean; onClick?: () => void }): ReactNode => {
    const { mobile, onClick } = options ?? {};
    const baseClass = mobile
      ? 'block py-2 text-sm font-medium transition-colors'
      : 'text-sm font-medium transition-colors duration-200 hover:text-accent-500 whitespace-nowrap';
    const activeClass = location.pathname === item.href ? 'text-accent-500' : textCls;
    return (
      <li key={item.href}>
        {item.external ? (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`${baseClass} ${textCls}`}
            onClick={onClick}
          >
            {item.label}
          </a>
        ) : (
          <Link
            to={item.href}
            className={`${baseClass} ${activeClass}`}
            onClick={onClick}
          >
            {item.label}
          </Link>
        )}
      </li>
    );
  };

  const startLongPress = () => {
    clearLongPress();
    pressStartRef.current = Date.now();
    pressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - pressStartRef.current;
      const progress = Math.min(elapsed / 3000, 1);
      setLongPressProgress(progress);
      if (progress >= 1) {
        clearLongPress();
        navigate('/admin');
      }
    }, 50);
  };

  const bgClass = scrolled
    ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';

  const textClass = scrolled
    ? 'text-foreground-900'
    : isLoginPage
      ? 'text-foreground-900'
      : 'text-white';

  return (
    <>
      {!isPlatformPage && (
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
        >
          <nav className="flex items-center justify-between px-6 md:px-10 lg:px-16 h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <div
                className="relative h-8 md:h-10 cursor-pointer select-none"
                onMouseDown={startLongPress}
                onMouseUp={clearLongPress}
                onMouseLeave={clearLongPress}
                onTouchStart={startLongPress}
                onTouchEnd={clearLongPress}
                onTouchCancel={clearLongPress}
              >
                <img
                  src={logo}
                  alt="优贝"
                  className="h-full w-auto object-contain"
                />
              </div>
              <span className={`font-heading font-bold text-lg md:text-2xl tracking-tight transition-colors duration-300 whitespace-nowrap ${textClass}`}>
                OPC在线社区
              </span>
            </Link>

            <ul className="hidden md:flex items-center gap-6 lg:gap-8">
              {navItems.map((item) => renderNavItem(item, textClass))}
            </ul>

            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-100/60 border border-background-200/40 text-sm font-medium text-foreground-900 hover:bg-background-200/60 transition-colors whitespace-nowrap"
                  >
                    <div className="w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-primary-950">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="max-w-[80px] truncate">{user?.name}</span>
                    <i className="ri-arrow-down-s-line text-xs text-foreground-400" />
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-background-50 border border-background-200/60 rounded-xl shadow-lg z-20 py-1">
                        <div className="px-4 py-2.5 border-b border-background-200/40">
                          <p className="text-sm font-medium text-foreground-900 truncate">{user?.name}</p>
                          <p className="text-xs text-foreground-400 truncate">{user?.loginName}</p>
                        </div>
                        <Link
                          to="/platform"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                        >
                          <i className="ri-dashboard-line" />
                          进入工作台
                        </Link>
                        <button
                          onClick={() => { logout(); setUserMenuOpen(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 transition-colors"
                        >
                          <i className="ri-logout-box-line" />
                          退出登录
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`text-sm font-medium transition-colors duration-200 hover:text-accent-500 whitespace-nowrap ${textClass}`}
                  >
                    登录
                  </Link>
                  <Link
                    to="/contact"
                    className="px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 bg-accent-500 text-primary-950 hover:bg-accent-400"
                  >
                    申请入驻
                  </Link>
                </>
              )}
            </div>

            <button
              className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors ${textClass}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </nav>

          {mobileOpen && (
            <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 px-6 py-4">
              <ul className="flex flex-col gap-3">
                {navItems.map((item) => renderNavItem(item, 'text-foreground-900', { mobile: true, onClick: () => setMobileOpen(false) }))}
                <li className="pt-2 flex flex-col gap-2">
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center gap-2 py-1">
                        <div className="w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-primary-950">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <span className="text-sm font-medium text-foreground-900">{user?.name}</span>
                      </div>
                      <Link
                        to="/platform"
                        className="block py-2 text-sm font-medium text-foreground-700"
                      >
                        进入工作台
                      </Link>
                      <button
                        onClick={() => { logout(); setMobileOpen(false); }}
                        className="block text-left py-2 text-sm font-medium text-foreground-600"
                      >
                        退出登录
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="block py-2 text-sm font-medium text-foreground-700"
                      >
                        登录
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-block text-center px-5 py-2.5 rounded-full text-sm font-medium bg-accent-500 text-primary-950"
                      >
                        申请入驻
                      </Link>
                    </>
                  )}
                </li>
              </ul>
            </div>
          )}
        </header>
      )}
    </>
  );
}